import {
  type Account,
  type AccountDetail,
  type Given,
  type GrantTerms,
  type PlanCatalog,
  type Subscription,
  accountDetailSchema,
  accountSchema,
  givenSchema,
  planCatalogSchema,
  subscriptionSchema,
} from "@crux/contracts";
import { z } from "zod";
import { problem } from "./errors.js";

/**
 * Plans and grants.
 *
 * These routes are not in the OpenAPI document the typed client is generated
 * from (`packages/contracts/openapi.json`, written by Node until it was
 * removed). So, like
 * People (`people.ts`), these are plain requests whose answers are parsed with
 * the shared schemas in `@crux/contracts`: nothing is trusted unparsed.
 */

const refusalSchema = z.object({
  message: z.string(),
  issues: z.array(z.object({ message: z.string() })).optional(),
});

async function send(
  path: string,
  init: { readonly method: string; readonly body?: unknown },
): Promise<Response> {
  return await fetch(path, {
    method: init.method,
    credentials: "same-origin",
    headers: init.body === undefined ? {} : { "content-type": "application/json" },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
}

/**
 * A refusal in the server's own words when it gave some — an engine that is
 * coming soon, an end date in the past — and the status as a sentence
 * otherwise.
 */
async function refused(response: Response, attempt: string): Promise<Error> {
  if (response.status === 429) {
    return new Error("Too many tries at once. Wait a while and try again.");
  }
  if (response.status === 400 || response.status === 409) {
    const parsed = refusalSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) {
      const first = parsed.data.issues?.[0]?.message;
      return new Error(first ?? parsed.data.message);
    }
  }
  return new Error(problem(response.status, attempt));
}

const workspacePath = (workspaceId: string, rest: string): string =>
  `/api/workspaces/${encodeURIComponent(workspaceId)}${rest}`;

export async function fetchPlanCatalog(): Promise<PlanCatalog> {
  const response = await send("/api/plans", { method: "GET" });
  if (!response.ok) throw await refused(response, "load the plans");
  return planCatalogSchema.parse(await response.json());
}

export async function fetchSubscription(workspaceId: string): Promise<Subscription> {
  const response = await send(workspacePath(workspaceId, "/subscription"), {
    method: "GET",
  });
  if (!response.ok) throw await refused(response, "load the plan");
  return subscriptionSchema.parse(await response.json());
}

export async function fetchGrantAccounts(): Promise<readonly Account[]> {
  const response = await send("/api/admin/accounts", { method: "GET" });
  if (!response.ok) throw await refused(response, "load the accounts");
  return z.array(accountSchema).parse(await response.json());
}

/** Who staff are giving access to, and on what terms. */
export interface GiveAccess extends GrantTerms {
  readonly email: string;
  readonly name: string;
  readonly company: string;
}

/** A new workspace for this person, on these terms, and an email to say so. */
export async function giveAccess(give: GiveAccess): Promise<Given> {
  const response = await send("/api/admin/accounts", { method: "POST", body: give });
  if (!response.ok) throw await refused(response, "give access");
  return givenSchema.parse(await response.json());
}

export async function fetchGrantAccount(workspaceId: string): Promise<AccountDetail> {
  const response = await send(`/api/admin/accounts/${encodeURIComponent(workspaceId)}`, {
    method: "GET",
  });
  if (!response.ok) throw await refused(response, "load the account");
  return accountDetailSchema.parse(await response.json());
}

export async function saveGrant(
  workspaceId: string,
  terms: GrantTerms,
): Promise<AccountDetail> {
  const response = await send(
    `/api/admin/accounts/${encodeURIComponent(workspaceId)}/grant`,
    { method: "PUT", body: terms },
  );
  if (!response.ok) throw await refused(response, "save the grant");
  return accountDetailSchema.parse(await response.json());
}

export async function endGrant(workspaceId: string): Promise<AccountDetail> {
  const response = await send(
    `/api/admin/accounts/${encodeURIComponent(workspaceId)}/grant`,
    { method: "DELETE" },
  );
  if (!response.ok) throw await refused(response, "end access");
  return accountDetailSchema.parse(await response.json());
}
